import { useEffect, useState } from "preact/hooks";

import type { ResolvedWidgetConfig } from "../config";
import {
  isViewportPinned,
  launcherOffset,
  launcherPoint,
  readLauncherPlacement,
  readObstructionBottom,
  snapLauncher,
  type LauncherBounds,
} from "./placement";
import type { LauncherDrag } from "./use-launcher-drag";
import { useIsMobile } from "./use-is-mobile";

/** 入口外壳的视口量测：固定定位、键盘避让和移动端判断都要用。 */
export interface LauncherViewport {
  height: number;
  top: number;
  width: number;
  left: number;
  keyboardOpen: boolean;
  drawerHeight: number;
}

function readLauncherViewport(): LauncherViewport {
  const visual = window.visualViewport;
  const height = visual?.height ?? window.innerHeight;
  // 回弹的越界偏移不能再次平移 fixed 外壳；键盘和缩放仍可在布局视口内平移。
  // 上限要用布局视口高度：iOS 双指放大时 innerHeight 跟着可视区域变小，拿它当上限会把外壳压回顶部。
  const top = Math.max(
    0,
    Math.min(
      visual?.offsetTop ?? 0,
      document.documentElement.clientHeight - height,
    ),
  );
  // 与 Base UI 的键盘识别边界一致；缩放不当作软件键盘。
  const keyboardOpen =
    !!visual && visual.scale === 1 && window.innerHeight - height > 60;
  return {
    height,
    top,
    width: visual?.width ?? window.innerWidth,
    left: visual?.offsetLeft ?? 0,
    keyboardOpen,
    // 抽屉里的 Base UI 会按键盘高度把抽屉抬起；外壳再跟着可见高度缩短就会抬两次。
    drawerHeight: keyboardOpen ? window.innerHeight - top : height,
  };
}

export interface LauncherChrome {
  mobile: boolean;
  viewport: LauncherViewport;
  /** 店面吸底购买栏占掉的底部高度。 */
  obstruction: number;
  /** 入口实际停靠的墙：买家拖过算买家放的，否则听商家配置。 */
  side: "left" | "right";
  launcherBottom: number;
  bounds: LauncherBounds;
  horizontalSpacing: number;
  bottomSpacing: number;
  bottom: number;
  draggable: boolean;
  launcherDrag: LauncherDrag;
  /** 入口位置（含拖动结果）合并进外观后的配置。 */
  placed: ResolvedWidgetConfig;
}

/**
 * 入口那一层外壳的几何：跟随 visualViewport、避让吸底购买控件、记忆买家拖到的位置。
 * 入口阶段和完整聊天窗共用，两边的入口必须停在同一处。
 * 避让高度在 state 初始化里同步量：接管时这是一棵新树，从 0 起步会先贴底一帧再弹回。
 */
export function useLauncherChrome(
  config: ResolvedWidgetConfig,
): LauncherChrome {
  const appearance = config.appearance;
  const mobile = useIsMobile();
  const [viewport, setViewport] =
    useState<LauncherViewport>(readLauncherViewport);
  const horizontalSpacing = appearance.horizontalSpacing ?? 16;
  const bottomSpacing = appearance.bottomSpacing ?? 16;
  const placementKey = `gonovus:launcher-placement:${config.shopDomain}`;
  const [placement, setPlacement] = useState(() =>
    readLauncherPlacement(placementKey),
  );
  const draggable = appearance.launcherDraggable !== false;
  // 商家关掉拖动后，买家以前拖出来的位置也不算数，入口回到后台配置的位置。
  const moved = draggable ? placement : undefined;
  const side = moved?.side ?? appearance.position ?? "right";

  const measureObstruction = (): number => {
    // 只读几何，不修改宿主元素。
    // ponytail: 仅识别这些语义选择器；试点主题出现其他购买控件时补对应选择器。
    const elements = document.querySelectorAll(
      '[name="add"], [name="checkout"], [data-mobile-bar], #shopify-pc__banner, #onetrust-banner-sdk, [data-testid="cookie-banner"]',
    );
    const rects = Array.from(elements)
      .filter(isViewportPinned)
      .map((element) => element.getBoundingClientRect());
    return readObstructionBottom(rects, viewport, side, {
      horizontal: horizontalSpacing,
      bottom: bottomSpacing,
    });
  };
  const [obstruction, setObstruction] = useState(measureObstruction);

  const bottom = Math.max(bottomSpacing, obstruction);
  const bounds: LauncherBounds = {
    width: viewport.width,
    height: viewport.height,
    horizontal: horizontalSpacing,
    bottom: bottomSpacing,
    floor: bottom,
  };
  const launcherBottom = launcherOffset(moved?.ratio ?? 1, bounds);
  const launcherDrag: LauncherDrag = {
    snap: (point) => launcherPoint(snapLauncher(point, bounds), bounds),
    place: (point) => {
      const next = snapLauncher(point, bounds);
      setPlacement(next);
      try {
        localStorage.setItem(placementKey, JSON.stringify(next));
      } catch {
        // 禁用存储时只在当前页面记住位置。
      }
    },
  };
  const placed = {
    ...config,
    appearance: { ...appearance, position: side },
  };
  useEffect(() => {
    const sync = () => {
      setViewport(readLauncherViewport());
    };
    window.visualViewport?.addEventListener("resize", sync);
    window.visualViewport?.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    return () => {
      window.visualViewport?.removeEventListener("resize", sync);
      window.visualViewport?.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, []);
  useEffect(() => {
    const sync = () => {
      setObstruction(measureObstruction());
    };
    // 首帧同步量过了，但渲染到监听挂上之间店面可能又变（吸底栏晚挂）；
    // 挂上时补量一次，之后交给观察器。
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });
    window.addEventListener("scroll", sync, { passive: true, capture: true });
    window.addEventListener("resize", sync);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
    // measureObstruction 依赖 viewport/side/spacing，逐个列出而不是把函数当依赖，
    // 免得每轮渲染都重建 observer。
  }, [side, horizontalSpacing, bottomSpacing, viewport]);
  return {
    mobile,
    viewport,
    obstruction,
    side,
    launcherBottom,
    bounds,
    horizontalSpacing,
    bottomSpacing,
    bottom,
    draggable,
    launcherDrag,
    placed,
  };
}
